import { createFileRoute, Link } from "@tanstack/react-router";
import { Check, X } from "lucide-react";
import { PageBanner } from "@/components/layout/PageBanner";
import { Button } from "@/components/ui/button";

const TITLE = "Shower Flooring Options: Pros & Cons | Homestead";
const DESCRIPTION =
  "Compare porcelain, stone, pebble, mosaic, and acrylic shower floors — durability, cost, and slip-resistance — by Searcy, AR remodelers.";
const URL_PATH = "https://homesteadconstruction.us/blog/shower-flooring-guide";

export const Route = createFileRoute("/blog/shower-flooring-guide")({
  head: () => ({
    meta: [
      { title: TITLE },
      { name: "description", content: DESCRIPTION },
      { property: "og:title", content: TITLE },
      { property: "og:description", content: DESCRIPTION },
      { property: "og:type", content: "article" },
      { property: "og:url", content: URL_PATH },
    ],
    links: [{ rel: "canonical", href: URL_PATH }],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "Article",
          headline: "Shower Flooring Options: Pros & Cons of Every Major Material",
          description: DESCRIPTION,
          author: { "@type": "Organization", name: "Homestead Construction" },
          publisher: { "@type": "Organization", name: "Homestead Construction" },
          mainEntityOfPage: URL_PATH,
        }),
      },
    ],
  }),
  component: ShowerFlooringGuide,
});

type Material = {
  name: string;
  blurb: string;
  pros: string[];
  cons: string[];
  bestFor: string;
  priceRange: string;
};

const MATERIALS: Material[] = [
  {
    name: "Porcelain Tile",
    blurb:
      "The most common shower floor material in modern remodels. Dense, low-absorption, and available in hundreds of looks — including convincing stone and wood-grain visuals.",
    pros: [
      "Extremely low water absorption (< 0.5%)",
      "Durable and resists chipping",
      "Huge style range — including large-format and textured anti-slip finishes",
      "Lower long-term maintenance than natural stone",
    ],
    cons: [
      "Grout lines still need to be sealed and cleaned",
      "Hard underfoot — no give",
      "Cheap porcelain can be slippery without a textured finish",
    ],
    bestFor: "Most master and guest showers — the safest all-around choice.",
    priceRange: "$$",
  },
  {
    name: "Natural Stone (Marble, Travertine, Slate)",
    blurb:
      "A premium, high-end look that turns the shower floor into a focal point. Each tile is unique, but stone is porous and demands a real maintenance routine.",
    pros: [
      "Unmatched natural beauty and resale appeal",
      "Slate offers excellent natural slip resistance",
      "Every floor is one-of-a-kind",
    ],
    cons: [
      "Porous — must be sealed every 6–12 months",
      "Prone to etching from soaps, shampoos, and hard water",
      "Higher upfront and lifetime cost",
      "Marble can stain permanently if neglected",
    ],
    bestFor: "Luxury master baths where the homeowner will commit to upkeep.",
    priceRange: "$$$$",
  },
  {
    name: "Pebble / River Rock",
    blurb:
      "A spa-like, textured floor that massages your feet. Looks incredible in photos, but the surface area of grout is dramatically higher than tile.",
    pros: [
      "Distinctive, organic look",
      "Excellent slip resistance from the rounded texture",
      "Comfortable, massage-like feel underfoot",
    ],
    cons: [
      "5–10× more grout than standard tile — much harder to clean",
      "Requires frequent sealing of both stones and grout",
      "Can trap soap scum and biofilm in crevices",
    ],
    bestFor: "Walk-in showers where the homeowner wants a resort feel and accepts the cleaning trade-off.",
    priceRange: "$$$",
  },
  {
    name: "Mosaic Tile (1″ or 2″)",
    blurb:
      "Small-format porcelain, ceramic, or glass mosaics. The default for curbed shower pans because the many grout lines conform to the slope of the drain.",
    pros: [
      "Conforms to sloped pans without lippage",
      "More grout = more inherent slip resistance",
      "Easy to mix colors, materials, and patterns",
    ],
    cons: [
      "Lots of grout to clean and seal",
      "Glass mosaics can be slippery if used alone",
      "Installation is labor-intensive (higher install cost than large-format)",
    ],
    bestFor: "Traditional curbed showers and any pan that needs to slope to a center drain.",
    priceRange: "$$",
  },
  {
    name: "Acrylic / Solid-Surface Pan",
    blurb:
      "A single molded pan with no grout lines at all. The fastest, most water-tight option, and the easiest to clean — though limited in style.",
    pros: [
      "Zero grout — easiest possible cleaning",
      "Lowest leak risk of any shower floor",
      "Lower install labor and faster turnaround",
      "Warm and slightly soft underfoot",
    ],
    cons: [
      "Limited colors, sizes, and styles",
      "Can scratch, dull, or yellow over years",
      "Lower perceived value at resale vs. tile or stone",
    ],
    bestFor: "Guest bathrooms, rentals, accessibility upgrades, and tight budgets/timelines.",
    priceRange: "$",
  },
];

const COMPARISON_ROWS: Array<{
  label: string;
  values: Record<string, string>;
}> = [
  {
    label: "Durability",
    values: {
      "Porcelain Tile": "Excellent",
      "Natural Stone (Marble, Travertine, Slate)": "Excellent",
      "Pebble / River Rock": "Very good",
      "Mosaic Tile (1″ or 2″)": "Excellent",
      "Acrylic / Solid-Surface Pan": "Good",
    },
  },
  {
    label: "Maintenance",
    values: {
      "Porcelain Tile": "Low",
      "Natural Stone (Marble, Travertine, Slate)": "High",
      "Pebble / River Rock": "High",
      "Mosaic Tile (1″ or 2″)": "Medium",
      "Acrylic / Solid-Surface Pan": "Very low",
    },
  },
  {
    label: "Slip Resistance",
    values: {
      "Porcelain Tile": "Depends on finish",
      "Natural Stone (Marble, Travertine, Slate)": "Very good (slate)",
      "Pebble / River Rock": "Excellent",
      "Mosaic Tile (1″ or 2″)": "Very good",
      "Acrylic / Solid-Surface Pan": "Good (textured pan)",
    },
  },
  {
    label: "Relative Cost",
    values: {
      "Porcelain Tile": "$$",
      "Natural Stone (Marble, Travertine, Slate)": "$$$$",
      "Pebble / River Rock": "$$$",
      "Mosaic Tile (1″ or 2″)": "$$",
      "Acrylic / Solid-Surface Pan": "$",
    },
  },
];

const FAQS = [
  {
    q: "What is the most low-maintenance shower floor?",
    a: "An acrylic or solid-surface pan, because it has no grout lines. Among tile options, large-format porcelain with epoxy grout is the easiest to keep clean.",
  },
  {
    q: "Is natural stone a bad idea in a shower?",
    a: "Not at all — slate and honed travertine perform beautifully. But every natural stone needs to be sealed regularly, and softer stones like marble can etch from everyday shower products if you don't keep up with it.",
  },
  {
    q: "Are pebble shower floors hard to clean?",
    a: "Yes, noticeably harder than flat tile. The rounded pebbles create far more grout surface area where soap scum, hair, and mildew can collect. Plan on sealing the stones and grout annually and using a soft brush each week.",
  },
  {
    q: "Why are shower floors usually small mosaic tiles?",
    a: "Small tiles can flex around the slope of a shower pan toward the drain without creating lippage. They also have more grout lines, which improves slip resistance.",
  },
];

function ShowerFlooringGuide() {
  return (
    <main className="min-h-screen bg-background">
      <PageBanner title="Shower Flooring Options: Pros & Cons" />

      <article className="container mx-auto max-w-4xl px-4 py-12 md:py-16">
        <p className="text-lg leading-relaxed text-muted-foreground">
          Choosing the right shower floor is one of the highest-impact decisions in any
          bathroom remodel. It affects how the shower feels underfoot, how often you'll
          be scrubbing it, how slippery it is when wet, and how the whole bathroom
          reads visually. Below is a no-fluff comparison of the five materials we
          install most often for homeowners in Searcy and across central Arkansas.
        </p>

        <section className="mt-12 space-y-12">
          {MATERIALS.map((m) => (
            <div key={m.name} className="rounded-2xl border border-border bg-card p-6 md:p-8">
              <div className="flex flex-wrap items-baseline justify-between gap-2">
                <h2 className="text-2xl font-bold text-foreground md:text-3xl">{m.name}</h2>
                <span className="text-sm font-semibold text-primary">{m.priceRange}</span>
              </div>
              <p className="mt-3 text-muted-foreground">{m.blurb}</p>

              <div className="mt-6 grid gap-6 md:grid-cols-2">
                <div>
                  <h3 className="text-sm font-bold uppercase tracking-wide text-foreground">
                    Pros
                  </h3>
                  <ul className="mt-3 space-y-2">
                    {m.pros.map((p) => (
                      <li key={p} className="flex gap-2 text-sm text-muted-foreground">
                        <Check className="mt-0.5 h-4 w-4 shrink-0 text-primary" />
                        <span>{p}</span>
                      </li>
                    ))}
                  </ul>
                </div>
                <div>
                  <h3 className="text-sm font-bold uppercase tracking-wide text-foreground">
                    Cons
                  </h3>
                  <ul className="mt-3 space-y-2">
                    {m.cons.map((c) => (
                      <li key={c} className="flex gap-2 text-sm text-muted-foreground">
                        <X className="mt-0.5 h-4 w-4 shrink-0 text-destructive" />
                        <span>{c}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>

              <p className="mt-6 border-t border-border pt-4 text-sm">
                <span className="font-semibold text-foreground">Best for: </span>
                <span className="text-muted-foreground">{m.bestFor}</span>
              </p>
            </div>
          ))}
        </section>

        <section className="mt-16">
          <h2 className="text-2xl font-bold text-foreground md:text-3xl">
            Side-by-Side Comparison
          </h2>
          <div className="mt-6 overflow-x-auto rounded-2xl border border-border">
            <table className="w-full min-w-[640px] text-left text-sm">
              <thead className="bg-muted">
                <tr>
                  <th className="p-3 font-semibold text-foreground"> </th>
                  {MATERIALS.map((m) => (
                    <th key={m.name} className="p-3 font-semibold text-foreground">
                      {m.name.split(" (")[0]}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {COMPARISON_ROWS.map((row) => (
                  <tr key={row.label} className="border-t border-border">
                    <th scope="row" className="p-3 font-semibold text-foreground">
                      {row.label}
                    </th>
                    {MATERIALS.map((m) => (
                      <td key={m.name} className="p-3 text-muted-foreground">
                        {row.values[m.name]}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>

        <section className="mt-16">
          <h2 className="text-2xl font-bold text-foreground md:text-3xl">
            How to Choose: A Quick Decision Framework
          </h2>
          <ol className="mt-6 space-y-4 text-muted-foreground">
            <li>
              <span className="font-semibold text-foreground">1. Start with maintenance honesty.</span>{" "}
              If you won't reseal stone every year, don't buy stone. Choose porcelain or an acrylic pan.
            </li>
            <li>
              <span className="font-semibold text-foreground">2. Match the floor to the pan style.</span>{" "}
              Curbed shower pans that slope to a center drain need small mosaics. Linear-drain
              showers can use large-format porcelain or stone slabs.
            </li>
            <li>
              <span className="font-semibold text-foreground">3. Check the slip rating.</span>{" "}
              Look for a DCOF (dynamic coefficient of friction) of 0.42 or higher on any tile
              going on a shower floor.
            </li>
            <li>
              <span className="font-semibold text-foreground">4. Think about resale.</span>{" "}
              Tile and stone almost always outperform acrylic at appraisal.
            </li>
            <li>
              <span className="font-semibold text-foreground">5. Budget for grout, not just tile.</span>{" "}
              Epoxy grout costs more upfront but stays cleaner for years and rarely needs sealing.
            </li>
          </ol>
        </section>

        <section className="mt-16">
          <h2 className="text-2xl font-bold text-foreground md:text-3xl">
            Frequently Asked Questions
          </h2>
          <div className="mt-6 space-y-6">
            {FAQS.map((f) => (
              <div key={f.q}>
                <h3 className="text-lg font-semibold text-foreground">{f.q}</h3>
                <p className="mt-2 text-muted-foreground">{f.a}</p>
              </div>
            ))}
          </div>
        </section>

        <aside className="mt-16 rounded-2xl bg-primary p-8 text-center text-primary-foreground md:p-10">
          <h2 className="text-2xl font-bold md:text-3xl">
            Planning a bathroom remodel in Searcy or central Arkansas?
          </h2>
          <p className="mx-auto mt-3 max-w-2xl text-primary-foreground/90">
            We'll walk your space, talk through which shower floor fits your budget and
            lifestyle, and give you a real, written quote — no pressure.
          </p>
          <div className="mt-6 flex flex-wrap justify-center gap-3">
            <Button asChild size="lg" variant="secondary">
              <Link to="/contact">Get a Free Quote</Link>
            </Button>
            <Button
              asChild
              size="lg"
              variant="outline"
              className="border-primary-foreground/40 bg-transparent text-primary-foreground hover:bg-primary-foreground hover:text-primary"
            >
              <Link to="/services">See Our Services</Link>
            </Button>
          </div>
        </aside>
      </article>

      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "FAQPage",
            mainEntity: FAQS.map((f) => ({
              "@type": "Question",
              name: f.q,
              acceptedAnswer: { "@type": "Answer", text: f.a },
            })),
          }),
        }}
      />
    </main>
  );
}
